import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Cog, Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("La password deve avere almeno 6 caratteri.");
    if (password !== confirm) return setError("Le password non coincidono.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0F17] p-6">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-lg bg-amber-500 flex items-center justify-center">
            <Cog className="h-6 w-6 text-slate-950" />
          </div>
          <span className="font-heading font-extrabold text-xl">MEKANIKA</span>
        </div>

        {!token ? (
          <div className="text-slate-300">
            <h2 className="font-heading text-2xl font-bold mb-2">Link non valido</h2>
            <p className="text-slate-500 text-sm">Il link di reset non è valido. Richiedine uno nuovo dalla pagina di login.</p>
            <Link to="/login" className="inline-block mt-6 text-amber-400 hover:text-amber-300 text-sm">← Torna al login</Link>
          </div>
        ) : done ? (
          <div data-testid="reset-success" className="text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold">Password aggiornata</h2>
            <p className="text-slate-500 text-sm mt-2">Verrai reindirizzato al login…</p>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-2xl font-bold">Nuova password</h2>
            <p className="text-slate-500 text-sm mt-1 mb-8">Imposta una nuova password per il tuo account.</p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="text-xs font-mono-data uppercase tracking-widest text-slate-400">Nuova password</label>
                <input type="password" data-testid="reset-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-2 w-full h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500 transition-colors" placeholder="••••••••" />
              </div>
              <div>
                <label className="text-xs font-mono-data uppercase tracking-widest text-slate-400">Conferma password</label>
                <input type="password" data-testid="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-2 w-full h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500 transition-colors" placeholder="••••••••" />
              </div>
              {error && <div data-testid="reset-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-4 py-2.5">{error}</div>}
              <button type="submit" disabled={loading} data-testid="reset-submit" className="w-full h-11 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Aggiorna password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
